import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { jobs } from '../data/jobs';
import { APPLICATION_URL } from '../constants/assets';

const { FiArrowLeft, FiCheckCircle } = FiIcons;

const JobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const job = jobs.find(j => j.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!job) navigate('/');
  }, [job, navigate]);

  if (!job) return null;

  return (
    <div className="min-h-screen bg-white pb-32">
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-16">
        <Link to="/" className="inline-flex items-center space-x-3 text-mag-blue font-black uppercase tracking-[0.2em] mb-8 md:mb-12 hover:text-mag-green transition-colors group">
          <SafeIcon icon={FiArrowLeft} className="w-6 h-6 transform group-hover:-translate-x-2 transition-transform" />
          <span>Back to Positions</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-8">
            <h1 className="text-5xl lg:text-8xl font-black text-mag-black mb-8 leading-[0.9] uppercase tracking-tighter">{job.title}</h1>
            
            <div className="flex flex-wrap gap-3 mb-12">
              <span className="bg-mag-blue text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest">{job.department}</span>
              <span className="bg-mag-green text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest">{job.type}</span>
            </div>

            <div className="space-y-16">
              <section>
                <h2 className="text-3xl font-black text-mag-black mb-8 uppercase tracking-tight border-l-8 border-mag-green pl-6">The Role</h2>
                <p className="text-xl md:text-2xl text-mag-black leading-relaxed mb-8 font-bold">{job.description}</p>
                <p className="text-mag-black/70 text-lg leading-relaxed font-medium">{job.overview}</p>
              </section>

              <section>
                <h2 className="text-3xl font-black text-mag-black mb-8 uppercase tracking-tight border-l-8 border-mag-green pl-6">What You'll Do</h2>
                <div className="grid gap-4">
                  {job.responsibilities.map((r, i) => (
                    <div key={i} className="flex items-start bg-gray-50 p-6 rounded-3xl border-2 border-gray-100 group hover:border-mag-blue transition-colors">
                      <SafeIcon icon={FiCheckCircle} className="w-7 h-7 text-mag-green mr-5 flex-shrink-0 mt-1" />
                      <span className="font-black text-mag-black text-lg uppercase tracking-tight leading-tight">{r}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-black text-mag-black mb-8 uppercase tracking-tight border-l-8 border-mag-green pl-6">What We Need</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {job.qualifications.map((q, i) => (
                    <li key={i} className="flex items-center space-x-4 font-bold text-mag-black bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <div className="w-3 h-3 bg-mag-green rounded-full flex-shrink-0"></div>
                      <span className="uppercase text-xs tracking-wide">{q}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-32 bg-mag-black text-white p-8 md:p-10 rounded-[2.5rem] border-b-[16px] border-mag-green shadow-2xl">
              <h3 className="text-3xl font-black mb-10 text-center uppercase tracking-widest text-mag-green">Quick Info</h3>
              
              <div className="space-y-6 mb-12">
                <div className="bg-white/5 p-6 rounded-[2rem] border border-white/10">
                  <div className="text-mag-green text-[10px] font-black uppercase tracking-widest mb-2">Compensation</div>
                  <div className="text-xl font-black">{job.salary}</div>
                </div>
                <div className="bg-white/5 p-6 rounded-[2rem] border border-white/10">
                  <div className="text-mag-green text-[10px] font-black uppercase tracking-widest mb-2">Primary Location</div>
                  <div className="text-xl font-black">{job.location}</div>
                </div>
              </div>

              <a 
                href={APPLICATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-mag-green text-white text-center py-6 rounded-2xl font-black text-xl hover:brightness-110 transition-all shadow-xl uppercase tracking-widest active:scale-95"
              >
                Apply Now
              </a>
              <p className="text-center mt-6 text-gray-500 font-bold uppercase text-[10px] tracking-widest">Takes 5 mins or less</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;